/**
 * Ancient Leshen — „The Witcher: Steel and Silver".
 *
 * HEART OF THE FOREST: ak Leshen v tomto ťahu už útočil, jeho ďalší útok má +3
 * (platí len vtedy, keď útočil Leshen, nie Vlk). Vlci majú pohyb 3, Leshen 1 —
 * boost pri manévri sa pripočíta každému k jeho vlastnému pohybu.
 *
 * SUMMON A WOLF: ak máš Vlka mimo dosky, polož ho na prázdny priestor podľa
 * efektu. Ak sú všetci na doske, jedného vyber a polož ho tam.
 * (Steel and Silver, „Ancient Leshen: Heart of the Forest")
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  drawCard,
  gainAction,
  getMap,
  healFighter,
  millDeck,
  removeFromBoard,
} from '../ops.js';
import { areEnemies } from '../players.js';
import type { FighterId, PlayerIndex, SpaceId } from '../types.js';
import { askMove, fighterOrNull, opponentDiscards } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const LESHEN = 'ancient-leshen';
const WOLF = 'wolves';
const WOLF_MOVE = 3;
const SUMMON = 'ancient-leshen.summon';

const leshenOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === LESHEN) ?? null;

const wolvesOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.filter((f) => f.owner === player && f.defId === WOLF);

/** Prázdne priestory v Leshenovej zóne. */
function emptyInLeshenZone(ctx: Ctx, player: PlayerIndex): SpaceId[] {
  const leshen = leshenOf(ctx, player);
  if (!leshen) return [];
  const map = getMap(ctx);
  return map.spaces
    .map((s) => s.id)
    .filter((id) => shareZone(map, id, leshen.spaceId) && !ctx.state.fighters.some((f) => f.spaceId === id));
}

/**
 * „Summon a Wolf in the Leshen's zone." Porazený Vlk sa vráti s plným
 * zdravím; keď sú obaja na doske, hráč vyberie, ktorý sa premiestni.
 */
function summonWolf(ctx: Ctx, player: PlayerIndex, source: string | null): void {
  const spaces = emptyInLeshenZone(ctx, player);
  if (spaces.length === 0) return;
  const fallen = (ctx.state.defeated ?? []).find((f) => f.owner === player && f.defId === WOLF);
  if (fallen) {
    askPlaceWolf(ctx, player, source, fallen.id, spaces);
    return;
  }
  const onBoard = wolvesOf(ctx, player);
  if (onBoard.length === 1) {
    askPlaceWolf(ctx, player, source, onBoard[0]!.id, spaces);
  } else if (onBoard.length > 1) {
    askChoice(ctx, {
      kind: 'option',
      player,
      source,
      label: 'Privolanie Vlka: všetci sú na doske — ktorého premiestniš do Leshenovej zóny?',
      optional: false,
      options: onBoard.map((wolf, i) => ({
        label: `Vlk ${i + 1}`,
        follow: { kind: 'effect' as const, effect: SUMMON, data: wolf.id },
      })),
    });
  }
}

function askPlaceWolf(ctx: Ctx, player: PlayerIndex, source: string | null, wolfId: FighterId, spaces: SpaceId[]): void {
  askChoice(ctx, {
    kind: 'place',
    player,
    source,
    label: 'Privolanie Vlka: polož ho na prázdny priestor v Leshenovej zóne',
    optional: false,
    fighterId: wolfId,
    spaces,
  });
}

/** Pomocný záznam: dokončenie privolania po výbere Vlka (FollowUp `effect`). */
const summonFollow: CardEffect = {
  onFollow: (ctx, choice, wolfId) => {
    if (!fighterOrNull(ctx, wolfId)) return;
    askPlaceWolf(ctx, choice.player, choice.source, wolfId, emptyInLeshenZone(ctx, choice.player));
  },
};

/** „Move each Wolf up to 3 spaces." — až po privolaní, preto cez `auto`. */
function askMoveWolvesLater(e: EffectCtx): void {
  askChoice(e.ctx, {
    kind: 'auto',
    player: e.player,
    source: e.cardId,
    label: 'Presun Vlkov',
    optional: false,
    follow: { kind: 'effect', effect: 'ancient-leshen.primeval-slam', data: 'wolves' },
  });
}

function askMoveWolves(ctx: Ctx, player: PlayerIndex, source: string | null, label: string): void {
  for (const wolf of wolvesOf(ctx, player)) {
    askMove(
      { ctx, cardId: source ?? '', player, fighterId: wolf.id, role: 'scheme', opponentPlayer: null, opponentFighterId: null, won: false },
      { fighters: [wolf.id], max: 3, label },
    );
  }
}

/** „Summon a Wolf in the Leshen's zone. Then move each Wolf up to 3 spaces." */
const primevalSlam: CardEffect = {
  after: (e) => {
    summonWolf(e.ctx, e.player, e.cardId);
    askMoveWolvesLater(e);
  },
  onFollow: (ctx, choice) => askMoveWolves(ctx, choice.player, choice.source, 'Presuň Vlka až o 3'),
};

/** „You may place the Leshen in any space." */
const flockOfBirds: CardEffect = {
  after: (e) => {
    const leshen = leshenOf(e.ctx, e.player);
    if (!leshen) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Flock of Birds: smieš položiť Leshena na ľubovoľný priestor',
      optional: true,
      fighterId: leshen.id,
      spaces: getMap(e.ctx).spaces.map((s) => s.id),
    });
  },
};

/** „Move the opposing fighter up to 2 spaces. Gain 1 action." */
const disturbingHowls: CardEffect = {
  after: (e) => {
    if (e.opponentFighterId) {
      askMove(e, { fighters: [e.opponentFighterId], max: 2, label: 'Disturbing Howls: presuň súperovu figúrku až o 2' });
    }
    gainAction(e.ctx, e.player);
  },
};

/** Koľko tvojich figúrok susedí so súperovou figúrkou v súboji. */
function friendsAdjacentToOpponent(e: EffectCtx): number {
  const target = fighterOrNull(e.ctx, e.opponentFighterId);
  if (!target) return 0;
  const around = new Set(neighbors(getMap(e.ctx), target.spaceId));
  return e.ctx.state.fighters.filter((f) => f.owner === e.player && around.has(f.spaceId)).length;
}

/** „Draw 1 card for each of your fighters adjacent to the opposing fighter." */
const natureAbounds: CardEffect = {
  after: (e) => {
    const count = friendsAdjacentToOpponent(e);
    for (let i = 0; i < count; i++) drawCard(e.ctx, e.player);
  },
};

/** „If you won the combat, your opponent discards 1 card." */
const commandTheForest: CardEffect = {
  after: (e) => {
    if (e.won) opponentDiscards(e, e.opponentPlayer);
  },
};

/** „Deal 1 damage to each opposing fighter adjacent to your fighter." */
const wilyFighting: CardEffect = {
  after: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    if (!me) return;
    const around = new Set(neighbors(getMap(e.ctx), me.spaceId));
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && around.has(f.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 1);
  },
};

/**
 * „If your fighter hasn't left their space this turn, draw 1 card."
 * Porovnáva sa so snímkou zo začiatku ťahu — kto odišiel a vrátil sa, ráta
 * sa ako nehybný (engine cestu v rámci ťahu nepamätá).
 */
const plantedFeet: CardEffect = {
  after: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    if (!me) return;
    const start = e.ctx.state.turn.startSpaces[me.id];
    if (start === undefined || start === me.spaceId) drawCard(e.ctx, e.player);
  },
};

/** „Add +2 to this card's value for each of your fighters adjacent to the opposing fighter." */
const harryingStrike: CardEffect = {
  during: (e, value) => value + 2 * friendsAdjacentToOpponent(e),
};

/**
 * „DURING COMBAT: Your opponent discards the top card of their deck. Add its
 * BOOST value to their card's value. AFTER COMBAT: Move each Wolf up to 3 spaces."
 */
const primevalGuardian: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const opponent = e.opponentPlayer;
    const top = opponent !== null ? e.ctx.state.players[opponent]?.deck[0] : undefined;
    if (!combat || opponent === null || !top) return;
    millDeck(e.ctx, opponent, 1);
    const side = e.role === 'defender' ? 'attacker' : 'defender';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += cardDef(e.ctx, top).boost;
  },
  after: (e) => askMoveWolves(e.ctx, e.player, e.cardId, 'Primeval Guardian: presuň Vlka až o 3'),
};

/**
 * „Summon a Wolf in the Leshen's zone. ONGOING: At the start of your turn,
 * the Leshen recovers 1 health. Discard this card at the end of your turn if
 * there are no Wolves in the Leshen's zone."
 */
const strengthOfThePack: CardEffect = {
  scheme: (e) => summonWolf(e.ctx, e.player, e.cardId),
  ongoing: {
    startOfTurn: (ctx, player) => {
      const leshen = leshenOf(ctx, player);
      if (leshen) healFighter(ctx, leshen.id, 1);
    },
    discardAtEnd: (ctx, player) => {
      const leshen = leshenOf(ctx, player);
      if (!leshen) return true;
      const map = getMap(ctx);
      return !wolvesOf(ctx, player).some((wolf) => shareZone(map, wolf.spaceId, leshen.spaceId));
    },
  },
};

/**
 * „Deal 1 damage to each opposing fighter in the Leshen's zone. Remove the
 * Leshen from the board. At the start of your next turn, place the Leshen in
 * any space. Then, draw 1 card." Mimo dosky je Leshen v `state.limbo` (ako
 * pri Vanish) a návrat s ťahaním rieši `startTurnEffects` (`returnDraw`).
 */
const vanishIntoMurder: CardEffect = {
  scheme: (e) => {
    const leshen = leshenOf(e.ctx, e.player);
    if (!leshen) return;
    const map = getMap(e.ctx);
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && shareZone(map, f.spaceId, leshen.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 1);
    if (!leshenOf(e.ctx, e.player)) return;
    removeFromBoard(e.ctx, leshen.id);
    const away = e.ctx.state.limbo?.find((f) => f.id === leshen.id);
    if (away) away.returnDraw = 1;
  },
};

export const leshenCards: EffectRegistry = {
  [SUMMON]: summonFollow,
  'ancient-leshen.primeval-slam': primevalSlam,
  'ancient-leshen.flock-of-birds': flockOfBirds,
  'ancient-leshen.disturbing-howls': disturbingHowls,
  'ancient-leshen.nature-abounds': natureAbounds,
  'ancient-leshen.command-the-forest': commandTheForest,
  'ancient-leshen.wily-fighting': wilyFighting,
  'ancient-leshen.planted-feet': plantedFeet,
  'ancient-leshen.harrying-strike': harryingStrike,
  'ancient-leshen.primeval-guardian': primevalGuardian,
  'ancient-leshen.strength-of-the-pack': strengthOfThePack,
  'ancient-leshen.vanish-into-murder': vanishIntoMurder,
};

export const leshenSpecial: HeroSpecial = {
  name: 'Heart of the Forest',
  /** Vlci majú pohyb 3 (na karte postavy), Leshen 1. */
  setup: (ctx, player) => {
    for (const wolf of wolvesOf(ctx, player)) wolf.move = WOLF_MOVE;
  },
  /** +3, ak Leshen v tomto ťahu už útočil — aktuálny útok je v zozname tiež, preto ≥ 2. */
  attackModifier: (ctx, player, fighterId) => {
    const leshen = fighterOrNull(ctx, fighterId);
    if (leshen?.defId !== LESHEN || ctx.state.turn.player !== player) return 0;
    const times = (ctx.state.turn.attackers ?? []).filter((id) => id === leshen.id).length;
    return times >= 2 ? 3 : 0;
  },
};
